import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const EyeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LightBulbIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const StarIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const MissionCard = ({ icon, title, children, variant = "default" }) => (
  <div className={`flex flex-col p-8 rounded-3xl border transition-all duration-300 ${
    variant === "primary"
      ? "bg-school-blue text-white border-school-blue/80 shadow-soft hover:shadow-md"
      : "bg-white border-gray-100 shadow-soft hover:shadow-md"
  }`}>
    <div className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl mb-6 shadow-sm ${
      variant === "primary"
        ? "bg-white/10 text-white"
        : "bg-school-blue/8 text-school-blue"
    }`}>
      {icon}
    </div>
    <h3 className={`text-xl md:text-2xl font-heading font-bold mb-4 tracking-tight leading-tight ${
      variant === "primary" ? "text-white" : "text-gray-900"
    }`}>
      {title}
    </h3>
    <p className={`text-sm md:text-base leading-7 ${
      variant === "primary" ? "text-white/65" : "text-gray-600"
    }`}>
      {children}
    </p>
  </div>
);

const ValueCard = ({ icon, title, description }) => (
  <div className="group flex items-start gap-5 p-7 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-school-gold/10 text-school-gold group-hover:bg-school-blue group-hover:text-white transition-colors duration-300 shadow-sm">
      {icon}
    </div>
    <div className="flex-1">
      <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight group-hover:text-school-gold transition-colors">
        {title}
      </h3>
      <p className="text-sm md:text-base text-gray-600 leading-7">{description}</p>
    </div>
  </div>
);

// ==================== MAIN ABOUT US COMPONENT ====================

const AboutUs = () => {
  const coreValues = [
    {
      icon: <StarIcon />,
      title: "Excellence in Education",
      description: "We maintain high academic standards and celebrate achievement in all forms, ensuring every student reaches their full potential."
    },
    {
      icon: <HeartIcon />,
      title: "Nurturing Environment",
      description: "We create a safe, welcoming space where students feel valued, heard, and supported in their educational journey."
    },
    {
      icon: <UsersIcon />,
      title: "Strong Community",
      description: "We build meaningful partnerships between students, families, educators, and the broader Bronx community."
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Our Story
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                About PS 85
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Empowering the students of today for the possibilities of tomorrow.
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== MISSION, VISION, FOCUS ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Foundation"
              title="Mission, Vision & Focus"
              description="The guiding principles that drive everything we do at PS 85."
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              <MissionCard icon={<AcademicCapIcon />} title="Mission Statement">
                At P.S. 85, we combine a supportive environment with an unshakeable commitment to academic rigor. We plan with the end in mind, utilizing data-driven targets and deliberate practice to transform student effort into conceptual mastery. By engaging every scholar in the productive struggle of independent work and peer-to-peer discourse, we foster a growth mindset and empower students as confident, self-directed leaders of their own learning. Great Expectations spark higher aspirations.
              </MissionCard>

              <MissionCard icon={<EyeIcon />} title="Vision Statement" variant="primary">
                Empowering the students of today for the possibilities of tomorrow.
              </MissionCard>

              <MissionCard icon={<LightBulbIcon />} title="Instructional Focus">
                If educators consistently maintain great expectations, know every student well and design strategic learning opportunities for student-directed work that engages them in culturally responsive and critical thinking experiences, then every student will thrive in an affirming environment and take greater ownership of their learning while empowering student voice.
              </MissionCard>
            </div>
          </Container>
        </section>

        {/* ==================== CORE VALUES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What We Believe"
              title="Our Core Values"
              description="The fundamental principles that shape our school culture and guide our daily practice."
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              {coreValues.map((value, index) => (
                <ValueCard key={index} {...value} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== ADDITIONAL INFO ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-brand-50 rounded-[2.25rem] -z-10 transform translate-x-4 translate-y-4" aria-hidden="true" />
                <img
                  src="https://3.files.edl.io/e0cc/21/09/10/154302-098709af-a2e5-4806-8787-7be5f8778ad3.jpg"
                  alt="Students at PS 85"
                  className="rounded-[2.25rem] shadow-soft w-full border border-gray-100"
                />
              </div>
              <div>
                <SectionHeader
                  eyebrow="Community"
                  title="A Place Where Every Child Belongs"
                  description="We're more than a school—we're a family committed to the success of every student."
                />
                <div className="mt-8 space-y-6 text-base md:text-lg text-gray-600 leading-relaxed">
                  <p>
                    Our dedicated team of educators works tirelessly to create a supportive learning environment where students feel safe to explore, question, and grow.
                  </p>
                  <p>
                    Through strong family partnerships and community engagement, we ensure that every child receives the support they need to succeed academically, socially, and emotionally.
                  </p>
                  <div className="pt-6">
                    <div className="pl-6 border-l-4 border-brand-500">
                      <p className="italic text-gray-800 font-medium text-xl mb-2">
                        "Great Expectations. Every Student. Every Day."
                      </p>
                      <cite className="text-sm text-gray-500 not-italic uppercase tracking-wider">Our Motto</cite>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CTA ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Ready to Learn More?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Discover how PS 85 can provide your child with a strong foundation for success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#visit"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Schedule a Visit
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default AboutUs;
